import { useMemo, useState } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { usePendingArchives } from "@/hooks/usePendingArchives";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { NewWorkspaceDialog } from "@/components/NewWorkspaceDialog";
import { Plus, Hourglass } from "lucide-react";

export const WorkspaceTabs = () => {
  const { workspaces, active, setActive } = useWorkspace();
  const { archives } = usePendingArchives();
  const [open, setOpen] = useState(false);

  const archiveDateById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of archives) map.set(a.workspace_id, a.archive_at);
    return map;
  }, [archives]);

  if (workspaces.length <= 1) return null;
  return (
    <>
      <div className="border-b border-border bg-card/50 px-6 flex items-center gap-1 overflow-x-auto">
        {workspaces.map((w, idx) => {
          const archiveAt = archiveDateById.get(w.id);
          const archiveLabel = archiveAt
            ? `Archives on ${new Date(archiveAt).toLocaleDateString()}`
            : undefined;
          return (
            <button
              key={w.id}
              onClick={() => setActive(w.id)}
              title={archiveLabel}
              className={cn(
                "py-3 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2",
                active?.id === w.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {w.workspace_label?.trim() || `Workspace ${idx + 1}`}
              {archiveAt && (
                <Hourglass className="h-3.5 w-3.5 text-yellow-600" aria-label={archiveLabel} />
              )}
              {w.account_tier === "admin_workspace" && (
                <Badge variant="secondary" className="text-[10px] py-0">admin</Badge>
              )}
            </button>
          );
        })}
        <button
          onClick={() => setOpen(true)}
          className="ml-1 py-3 px-3 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 whitespace-nowrap"
          title="Create a new workspace"
        >
          <Plus className="h-4 w-4" /> New workspace
        </button>
      </div>
      <NewWorkspaceDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
